import { Formik } from 'formik';
import { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Label } from '@mui/icons-material';
import { ProviderFilterTypeEnum } from '@uplift/types';
import { Alert, Button, CheckboxGroup } from '@uplift/ui-v2';

import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components';
import { ProviderFiltersNextData } from '../types';
import { CareType } from '@uplift/api';

export type ProviderFiltersProps = {
  onNext: (data: ProviderFiltersNextData) => void;
  onBack: () => void;
  providerFilters: ProviderFilterTypeEnum[];

  careType: CareType;
  hideAlert?: boolean;
};

export const ProviderFilters: React.FC<ProviderFiltersProps> = props => {
  const { classes } = useProviderFiltersStyles();

  const providerLabel = useMemo(() => {
    if (props.careType && (props.careType === CareType.therapy || props.careType === CareType.therapyAndPsychiatry)) {
      return <Typography variant="inherit" color="secondary" component="span">therapist</Typography>;
    }

    return 'provider';
  }, [ props.careType ]);

  return (
    <Formik
      initialValues={{ providerFilters: props.providerFilters }}
      onSubmit={props.onNext}
    >
      {formik => (
        <StepContainer>
          {!props.hideAlert && <ProviderFiltersAlert careType={props.careType} />}
          <StepContent>
            <StepHeading>What matters to you when choosing a&nbsp;{providerLabel}?</StepHeading>
            <StepForm onSubmit={formik.handleSubmit}>

              <CheckboxGroup
                name="providerFilters"
                className={classes['ml-1']}
                options={[
                  {
                    label: 'Has a specific identity',
                    value: 'identity',
                    description: <Typography variant="body2" className={classes['mb-1']}>Filter by gender, faith, or ethnicity</Typography>,
                    className: 'mb-4'
                  },
                  {
                    label: 'Has experience with specific communities',
                    value: 'experience',
                    description: <Typography variant="body2">Filter by experience with groups ranging from social, ethnic, and faith communities</Typography>
                  }
                ]}
              />

            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.values.providerFilters.length} />
            <Button color="secondary" onClick={() => props.onNext({ providerFilters: [] })}>Go straight to results</Button>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const useProviderFiltersStyles = makeStyles({ name: 'ProviderFilters' })(theme => ({
  'mb-1': {
    marginBottom: theme.spacing(1)
  },
  'ml-1': {
    marginLeft: theme.spacing(1)
  }
}));

type ProviderFiltersAlertProps = {
  careType: CareType;
};

const ProviderFiltersAlert: React.FC<ProviderFiltersAlertProps> = (props) => {
  const { classes } = useAlertStyles();

  if (props.careType === CareType.therapyAndPsychiatry) {
    return (
      <Box bgcolor="success.background">
        <Alert classes={{ root: classes.root }} title="Start booking with a therapist" icon={<Label />}>
          First, you'll match with a therapist. Then, you'll book your medication management consultation.
        </Alert>
      </Box>
    );
  }

  return (
    <Box bgcolor="success.background">
      <Alert classes={{ root: classes.root }} title="We have providers who fit your needs">
        Let’s look at some other factors that can help narrow down your choice.
      </Alert>
    </Box>
  );
};

const useAlertStyles = makeStyles({ name: 'Alert' })(theme => ({
  root: {
    backgroundColor: theme.palette.success.background,
    border: 'none',
    maxWidth: `calc(640px + ${theme.spacing(4)})`,
    margin: '0 auto',
    boxSizing: 'border-box',
  },
}));
